<template>
    <div :aria-expanded="String(popoverOpen)" class="user-extended" v-on="listener" @keydown.space.prevent>
        <div class="user-image" v-if="showImage" role="img" :aria-label="'Bild von ' + name" @click="clickImage">
            <div v-if="_image && !svg" class="user-image-holder" :class="shape" :style="{backgroundImage: 'url(' + _image + ')'}" aria-hidden="true"></div>
            <div v-else-if="_image && svg" class="user-image-holder" :class="shape" v-html="_image" aria-hidden="true"></div>
            <div v-else class="user-image-holder" :class="shape" :style="holderStyle" aria-hidden="true"><i class="material-icons md-24" aria-hidden="true"
                :class="{'md-dark': !color}" role="img">{{defaultIcon}}</i>
            </div>
            <slot name="image-content" aria-hidden="true"/>
        </div>
        <template v-if="showName">
            <div class="user-data">
                <div class="flex">
                    <span class="user-name">
                        <slot name="name" :username="name"><strong>{{name}}</strong></slot>
                        <slot name="email">
                            <span v-if="!isExportRequest && showContact && $store.state.layoutData.contactMail" v-interact="contact"
                                class="icon-interact contact-mail intranet-headline-color">
                                <i aria-hidden="true" class="material-icons md-16" role="img">email</i>
                            </span>
                        </slot>
                    </span>
                </div>
                <span v-if="$slots.default" class="user-sub"><slot/></span>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    import tinycolor from 'tinycolor2';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import {getAbbreviationAvatar} from '@/filters/username';
    import {removeSurnameAffix} from '@/library/utils';

    @Component({
        name: 'PersonBox',
        emits: ['contact', 'image-clicked'],
    })
    export default class PersonBox extends Vue
    {
        @Prop({
            type: String,
            default: '',
            required: false,
        }) name: string;

        @Prop({
            type: String,
        }) image: string;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) showName: boolean;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) showImage: boolean;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) abbreviationName: string;

        @Prop({
            type: [Boolean, String],
            default: true,
            required: false,
        }) abbreviationAvatar: boolean | string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) reverseAbbreviation: boolean;

        @Prop({
            type: String,
            default: 'person',
            required: false,
        }) defaultIcon: string;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) color: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) showContact: boolean;

        @Prop({
            type: String,
            default: 'circle',
            required: false,
            validator(value: string)
            {
                return ['circle', 'square'].includes(value);
            },
        }) shape: string;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) useIntranetColors: boolean;

        @Prop({
            type: Object,
            default: () =>
            {
                return {};
            },
            validator(listener)
            {
                for (let value of Object.values(listener))
                {
                    if (typeof value !== 'function')
                    {
                        return false;
                    }
                }

                return true;
            },
        }) listener: Function[];

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) popoverOpen: boolean;

        get _image(): string
        {
            let image: string = '';

            if (this.image)
            {
                image = this.image;
            }
            else if (this.abbreviationAvatar/* && this.name.split(' ').length > 1*/)
            {
                let abbreviation;
                if ('boolean' === typeof this.abbreviationAvatar)
                {
                    let split    = removeSurnameAffix(this.abbreviationName || this.name).split(' ');
                    abbreviation = split[0].substr(0, 1);

                    if (split.length > 1)
                    {
                        abbreviation += split[split.length - 1].substr(0, 1);
                    }
                    else
                    {
                        abbreviation += split[0].substr(1, 1);
                    }
                }
                else
                {
                    abbreviation = this.abbreviationAvatar;
                }

                if(this.reverseAbbreviation)
                {
                    abbreviation = abbreviation.split('').reverse().join('');
                }

                image = getAbbreviationAvatar(abbreviation, this.shape, this.useIntranetColors, 40, false);
            }

            return image;
        }

        get svg(): boolean
        {
            return this._image.substr(0, 4) === '<svg';
        }

        get holderStyle(): Partial<CSSStyleDeclaration>
        {
            let styles: Partial<CSSStyleDeclaration> = {};
            if (this.color)
            {
                let color              = tinycolor(this.color);
                styles.borderColor     = color.setAlpha(0.3).toRgbString();
                styles.borderStyle     = 'solid';
                styles.borderWidth     = '1px';
                styles.backgroundColor = color.setAlpha(0.13).toRgbString();
                styles.color           = this.color;
            }

            return styles;
        }

        contact()
        {
            this.$emit('contact');
        }

        clickImage()
        {
            this.$emit('image-clicked');
        }
    };
</script>